import {
  ArcElement,
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js'
import Datalabels from 'chartjs-plugin-datalabels'
import moment from 'moment'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Chart, Line, Pie } from 'react-chartjs-2'
import { useForm } from 'react-hook-form'

import {
  ChevronRightIcon,
  PresentationChartLineIcon,
} from '@heroicons/react/24/outline'

import useServerOrderProd from '../api/useServerOrderProd'
import DateInput from '../components/forms/DateInput'
import InlineRadio, { InlineRadioData } from '../components/forms/InlineRadio'
import UnlimitedBadge from '../components/misc/badges/UnlimitedBadge'
import Button from '../components/misc/Button'
import EmptyList from '../components/misc/EmptyList'
import GenericTable, {
  DataTableInterface,
} from '../components/misc/GenericTable'
import GenericToggle from '../components/misc/GenericToggle'
import LastOperationsCard from '../components/misc/LastOperationsCard'
import PendingDispatch from '../components/misc/PendingDispatch'
import SpinnerLoading from '../components/misc/SpinnerLoading'
import UnderLimitProductsCard from '../components/misc/UnderLimitProductsCard'
import useProduct from '../hooks/useProduct'
import { GetGraphDataParams, useGraphData } from '../store/actions/globals'
import { useAppSelector } from '../store/hooks'
import { printPriceWithCommasAndPeriods } from '../utils/functions'
import { getMaxValue, getPercent, prettyNumber } from '../utils/helpers'
import { Currency } from '@/interfaces/Interfaces'
// import { BusinessInterface } from '@/interfaces/ServerInterfaces'
import {
  GraphDataInterface,
  GroupGraphData,
} from '@/store/slices/auxiliarSlice'
import { cn } from '@/lib/utils'
import { ErrorOverlay } from '@/components/ui/error-overlay'
import { useNavigate } from 'react-router-dom'
import { QueryObserverResult, RefetchOptions } from '@tanstack/react-query'

ChartJS.register(
  BarController,
  LinearScale,
  CategoryScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Datalabels
)

type RefetchFn = (
  _options?: RefetchOptions
) => Promise<QueryObserverResult<GraphDataInterface, Error>>

const HomeGroup = ({
  onErrorChange,
  onRefetch,
}: {
  onErrorChange?: (_err: boolean) => void
  onRefetch?: (_fn: RefetchFn) => void
}) => {
  // const { graphsData, loading } = useAppSelector((state) => state.auxiliar)
  const { business } = useAppSelector((state) => state.init)
  const { control, watch } = useForm()
  // const dispatch = useAppDispatch()

  const { formatPrice, convertCurrency } = useProduct()

  const availableCurrencies = business?.availableCurrencies
  const mainCurrency = business?.mainCurrency

  const [activeCurrency, setActiveCurrency] = useState<string>(
    mainCurrency ?? 'CUP'
  )

  // const [customRange, setCustomRange] = useState(false)

  const timeFilter = watch('timeFilter') as DateMode
  const dateFrom = watch('dateFrom')
  const dateTo = watch('dateTo')

  type DateMode = 'yesterday' | 'today' | 'week' | 'month' | 'year' | 'custom'

  const autoParams: GetGraphDataParams | undefined = useMemo(() => {
    if (!business) return undefined

    // si es custom, chequear fechas
    if (timeFilter === 'custom' && (!dateFrom || !dateTo)) return undefined

    // castear timeFilter a DateMode de forma segura
    const mode = timeFilter as DateMode

    const base = { businessMode: 'group' as const, business }

    switch (mode) {
      case 'yesterday':
        return {
          ...base,
          dateMode: 'yesterday',
          dateRange: {
            dateFrom: moment().subtract(1, 'd').format('YYYY-MM-DD'),
            dateTo: moment().subtract(1, 'd').format('YYYY-MM-DD'),
          },
        }
      case 'week':
        return {
          ...base,
          dateMode: 'week',
          dateRange: {
            dateFrom: moment().startOf('week').format('YYYY-MM-DD'),
            dateTo: moment().format('YYYY-MM-DD'),
          },
        }
      case 'month':
        return {
          ...base,
          dateMode: 'month',
          dateRange: {
            dateFrom: moment().startOf('month').format('YYYY-MM-DD'),
            dateTo: moment().format('YYYY-MM-DD'),
          },
        }
      case 'year':
        return {
          ...base,
          dateMode: 'year',
          dateRange: {
            dateFrom: moment().startOf('year').format('YYYY-MM-DD'),
            dateTo: moment().format('YYYY-MM-DD'),
          },
        }
      case 'custom':
        return { ...base, dateMode: 'custom', dateRange: { dateFrom, dateTo } }
      default:
        return {
          ...base,
          dateMode: 'today',
          dateRange: {
            dateFrom: moment().format('YYYY-MM-DD'),
            dateTo: moment().format('YYYY-MM-DD'),
          },
        }
    }
  }, [timeFilter, dateFrom, dateTo, business])

  const params = autoParams

  const {
    data: graphsData,
    isLoading,
    isError,
    refetch,
  } = useGraphData(params) as {
    data: GraphDataInterface | undefined
    isLoading: boolean
    isError: boolean
    refetch: RefetchFn
  }

  useEffect(() => {
    onErrorChange?.(isError)
  }, [isError, onErrorChange])

  useEffect(() => {
    if (!onRefetch) return
    onRefetch(refetch)
  }, [refetch, onRefetch])

  // useEffect(() => {
  //   if (!graphsData) {
  //     dispatch(
  //       getGraphData({
  //         dateMode: 'today',
  //         dateRange: {
  //           dateFrom: moment().format('YYYY-MM-DD'),
  //           dateTo: moment().format('YYYY-MM-DD'),
  //         },
  //         businessMode: 'group',
  //       })
  //     )
  //   } else if (getFieldState('timeFilter').isDirty) {
  //     switch (watch('timeFilter')) {
  //       case 'yesterday':
  //         customRange && setCustomRange(false)
  //         dispatch(
  //           getGraphData({
  //             dateMode: 'yesterday',
  //             dateRange: {
  //               dateFrom: moment().subtract(1, 'd').format('YYYY-MM-DD'),
  //               dateTo: moment().subtract(1, 'd').format('YYYY-MM-DD'),
  //             },
  //             businessMode: 'group',
  //           })
  //         )
  //         break

  //       case 'week':
  //         customRange && setCustomRange(false)
  //         dispatch(
  //           getGraphData({
  //             dateMode: 'week',
  //             dateRange: {
  //               dateFrom: moment().startOf('week').format('YYYY-MM-DD'),
  //               dateTo: moment().format('YYYY-MM-DD'),
  //             },
  //             businessMode: 'group',
  //           })
  //         )
  //         break

  //       case 'month':
  //         customRange && setCustomRange(false)
  //         dispatch(
  //           getGraphData({
  //             dateMode: 'month',
  //             dateRange: {
  //               dateFrom: moment().startOf('month').format('YYYY-MM-DD'),
  //               dateTo: moment().format('YYYY-MM-DD'),
  //             },
  //             businessMode: 'group',
  //           })
  //         )
  //         break

  //       case 'year':
  //         customRange && setCustomRange(false)
  //         dispatch(
  //           getGraphData({
  //             dateMode: 'year',
  //             dateRange: {
  //               dateFrom: moment().startOf('year').format('YYYY-MM-DD'),
  //               dateTo: moment().format('YYYY-MM-DD'),
  //             },
  //             businessMode: 'group',
  //           })
  //         )
  //         break

  //       case 'custom':
  //         setCustomRange(true)
  //         break

  //       default:
  //         customRange && setCustomRange(false)
  //         dispatch(
  //           getGraphData({
  //             dateMode: 'today',
  //             dateRange: {
  //               dateFrom: moment().format('YYYY-MM-DD'),
  //               dateTo: moment().format('YYYY-MM-DD'),
  //             },
  //             businessMode: 'group',
  //           })
  //         )
  //         break
  //     }
  //   }
  // }, [timeFilter])

  //Sales Report Graph -----------------------------------------------------------------------------------------

  const GRAPH_COLORS: { tw: string; hx: string }[] = [
    { tw: 'sky-500', hx: '#0ea5e9' },
    { tw: 'orange-500', hx: '#ea580c' },
    { tw: 'blue-400', hx: '#60a5fa' },
    { tw: 'green-400', hx: '#22c55e' },
    { tw: 'indigo-600', hx: '#5850ec' },
    { tw: 'purple-400', hx: '#c084fc' },
    { tw: 'yellow-300', hx: '#fde047' },
    { tw: 'yellow-500', hx: '#ca8a04' },
    { tw: 'red-200', hx: '#fecaca' },
    { tw: 'orange-300', hx: '#fdba74' },
    { tw: 'blue-500', hx: '#2563eb' },
    { tw: 'green-200', hx: '#86efac' },
    { tw: 'indigo-300', hx: '#a5b4fc' },
    { tw: 'purple-800', hx: '#6b21a8' },
  ]

  const labels: { idx: number; name: string; color: string }[] = []
  const profitLabel: string[] = []
  const salesLabel: string[] = []
  const incomesLabel: string[] = []
  const salesProfit: number[] = []
  const salesCost: number[] = []
  const sales: number[] = []
  const incomes: number[] = []
  const colorSalesPie: string[] = []
  const colorIncomesPie: string[] = []
  //-------------------------------------------------------------------------------------------

  //Manage Filter business button ------------------------------------------------------------------------
  const [inactiveBusiness, setInactiveBusiness] = useState<number[]>([])

  const filterBusiness = (idx: number) => {
    const index = inactiveBusiness.findIndex((item) => item === idx)
    if (index !== -1) {
      setInactiveBusiness(inactiveBusiness.filter((item) => item !== idx))
    } else {
      setInactiveBusiness([...inactiveBusiness, idx])
    }
  }

  const [pieStatus, setPieStatus] = useState<'incomes' | 'sales' | null>(null)

  const totals: { sales: number; incomes: number } = useMemo(() => {
    const sales =
      graphsData?.groupData?.reduce((total, value, idx) => {
        if (!inactiveBusiness.includes(idx)) {
          const converted = convertCurrency(
            {
              price: value.totalSalesMainCurerncy,
              codeCurrency: mainCurrency ?? 'USD',
            },
            activeCurrency
          )
          return total + (converted?.price ?? 0)
        }
        return total
      }, 0) ?? 0

    const incomes =
      graphsData?.groupData?.reduce((total, value, idx) => {
        if (!inactiveBusiness.includes(idx)) {
          const converted = convertCurrency(
            {
              price: value.totalIncomesMainCurrency,
              codeCurrency: mainCurrency ?? 'USD',
            },
            activeCurrency
          )
          return total + (converted?.price ?? 0)
        }
        return total
      }, 0) ?? 0

    if (incomes !== 0 && pieStatus !== 'sales') {
      setPieStatus('incomes')
    } else if (sales !== 0 && pieStatus !== 'incomes') {
      setPieStatus('sales')
    } else {
      setPieStatus(null)
    }

    return { sales, incomes }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphsData?.groupData, inactiveBusiness, pieStatus, activeCurrency])

  if (graphsData?.groupData?.length !== 0)
    graphsData?.groupData?.forEach((data, idx) => {
      let pickedColor = idx
      const ratio = Math.floor(idx / GRAPH_COLORS.length)
      if (ratio > 0) {
        pickedColor = idx - Math.round(ratio * GRAPH_COLORS.length)
      }
      if (
        data.grossProfit !== 0 ||
        data.totalCost !== 0 ||
        data.totalSalesMainCurerncy !== 0 ||
        data.totalIncomesMainCurrency !== 0
      ) {
        labels.push({
          idx,
          name: data.name,
          color: GRAPH_COLORS[pickedColor].tw,
        })
      }
      if (
        (data.grossProfit !== 0 || data.totalCost !== 0) &&
        !inactiveBusiness.includes(idx)
      ) {
        profitLabel.push(data.name)
        salesProfit.push(
          convertCurrency(
            { price: data.grossProfit, codeCurrency: mainCurrency ?? 'CUP' },
            activeCurrency
          ).price ?? 0
        )
        salesCost.push(
          convertCurrency(
            { price: data.totalCost, codeCurrency: mainCurrency ?? 'CUP' },
            activeCurrency
          ).price ?? 0
        )
      }
      if (
        data.totalSalesMainCurerncy !== 0 &&
        !inactiveBusiness.includes(idx)
      ) {
        salesLabel.push(data.name)
        sales.push(data.totalSalesMainCurerncy)
        colorSalesPie.push(GRAPH_COLORS[pickedColor].hx)
      }
      if (
        data.totalIncomesMainCurrency !== 0 &&
        !inactiveBusiness.includes(idx)
      ) {
        incomesLabel.push(data.name)
        incomes.push(data.totalIncomesMainCurrency)
        colorIncomesPie.push(GRAPH_COLORS[pickedColor].hx)
      }
    })

  //---------------------------------------------------------------------------------------------------

  const btnInfo = [
    { value: 'yesterday', label: 'Ayer' },
    { value: 'today', label: 'Hoy' },
    { value: 'week', label: 'Esta semana' },
    { value: 'month', label: 'Este mes' },
    { value: 'year', label: 'Este año' },
    { value: 'custom', label: 'Personalizado' },
  ]

  // const onSubmit: SubmitHandler<Record<string, string>> = (data) => {
  //   if (!business) return undefined
  //   if (data.timeFilter === 'custom' && data.dateFrom && data.dateTo) {
  //     setCustomParams({
  //       dateMode: 'custom',
  //       dateRange: {
  //         dateFrom: data.dateFrom,
  //         dateTo: data.dateTo,
  //       },
  //       businessMode: 'group',
  //       business,
  //     })
  //   }
  // }

  let dateRange: { dateFrom?: string; dateTo: string } | null = null
  switch (graphsData?.dateMode) {
    case 'yesterday':
      dateRange = {
        dateTo: moment().subtract(1, 'd').format('D [de] MMMM'),
      }
      break

    case 'week':
      dateRange = {
        dateFrom: moment().startOf('week').format('D [de] MMMM'),
        dateTo: moment().format('D [de] MMMM'),
      }
      break

    case 'month':
      dateRange = {
        dateFrom: moment().startOf('month').format('D [de] MMMM'),
        dateTo: moment().format('D [de] MMMM'),
      }
      break

    case 'year':
      dateRange = {
        dateFrom: moment().startOf('year').format('DD/MM/YYYY'),
        dateTo: moment().format('DD/MM/YYYY'),
      }
      break

    default:
      dateRange = {
        dateTo: moment().format('D [de] MMMM'),
      }
      break
  }

  type IncomeSummary = Record<
    string,
    {
      Cash: number
      Transfer: number
    }
  >

  interface IncomeSummaryResult {
    codeCurrency: string
    cash: number
    transfer: number
  }

  const getTotalIncomeFunction = (
    items: GroupGraphData[]
  ): IncomeSummaryResult[] => {
    const incomesSummary: IncomeSummary = {}

    items.forEach((item) => {
      item.totalIncomes.forEach((income) => {
        const { amount, codeCurrency, paymentWay } = income

        if (!incomesSummary[codeCurrency]) {
          incomesSummary[codeCurrency] = { Cash: 0, Transfer: 0 }
        }

        if (paymentWay === 'CASH') {
          incomesSummary[codeCurrency].Cash += amount
        } else if (paymentWay === 'TRANSFER') {
          incomesSummary[codeCurrency].Transfer += amount
        }
      })
    })

    // Convertir el objeto a un arreglo con la estructura deseada
    let incomesSummaryArray = Object.keys(incomesSummary).map(
      (codeCurrency) => ({
        codeCurrency,
        cash: incomesSummary[codeCurrency].Cash,
        transfer: incomesSummary[codeCurrency].Transfer,
      })
    )

    // Rellenar con 0 las monedas que no tengan valores en Cash o en Transfer
    return (incomesSummaryArray = incomesSummaryArray.map((item) => ({
      ...item,
      cash: item.cash || 0,
      transfer: item.transfer || 0,
    })))
  }

  const totalIncomes: {
    codeCurrency: string
    transfer: number
    cash: number
  }[] = getTotalIncomeFunction(graphsData?.groupData ?? [])

  const [selectedIncome, setselectedIncome] = useState<string>('general')

  const chartRef = useRef()

  return (
    <div className="pb-10">
      <div className="w-full flex-col py-5">
        <div className="flex">
          <InlineRadio
            control={control}
            data={btnInfo}
            defaultValue={'today'}
            name="timeFilter"
            // defaultValue={graphsData?.dateMode ?? "today"}
          />
        </div>

        <div className="flex justify-center gap-3">
          {timeFilter === 'custom' ? (
            <>
              <form
                className="block gap-3 py-1 md:flex"
                // onSubmit={handleSubmit(onSubmit)}
              >
                <DateInput
                  control={control}
                  defaultValue={graphsData?.dateRange?.dateFrom}
                  label="Desde"
                  name="dateFrom"
                  rules={{ required: 'inserte una fecha de inicio' }}
                  untilToday
                />
                <DateInput
                  control={control}
                  defaultValue={
                    graphsData?.dateRange?.dateTo ??
                    moment().format('YYYY-MM-DD')
                  }
                  label="Hasta"
                  name="dateTo"
                  rules={{ required: 'inserte una fecha de fin' }}
                  untilToday
                />
                {/* <div className="flex items-end">
                  <Button color="slate-600" name="Aplicar" type="submit" />
                </div> */}
              </form>
            </>
          ) : (
            <div className="inline-flex items-center justify-center gap-5 py-3">
              {dateRange && (
                <>
                  {dateRange.dateFrom && (
                    <>
                      <div className="rounded-md border border-gray-300 px-2 py-1">
                        {dateRange.dateFrom}
                      </div>
                      <ChevronRightIcon className="h-5 text-gray-400" />{' '}
                    </>
                  )}
                  <div className="rounded-md border border-gray-300 px-2 py-1">
                    {dateRange.dateTo}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      {isLoading ? (
        <SpinnerLoading />
      ) : (
        <>
          <div className="flex items-center justify-center gap-5 py-10">
            {availableCurrencies?.map((curr: Currency, index: number) => {
              return (
                <Button
                  action={() => {
                    setActiveCurrency(curr.code)
                  }}
                  color={activeCurrency === curr.code ? 'slate-600' : 'white'}
                  key={index}
                  name={curr.code}
                  textColor={
                    activeCurrency === curr.code ? 'white' : 'slate-600'
                  }
                />
              )
            })}
          </div>

          <div className="inline-flex w-full justify-center gap-2 pb-5">
            {labels.map((item, idx) => {
              const color = item.color
              return (
                <button
                  className={`rounded-lg border border-gray-400 p-1 shadow-md bg-${color} text-xs ${
                    inactiveBusiness.includes(item.idx) ? 'line-through' : ''
                  }`}
                  key={idx}
                  onClick={() => filterBusiness(item.idx)}
                >
                  {item.name}
                </button>
              )
            })}
          </div>
          {/* -------------------------------------------------- */}
          <div>
            {pieStatus !== null && (
              <div className="flex w-full flex-col items-center justify-center md:flex-row">
                {/* Grafico de Pastel */}
                <div className="mr-0 md:mr-10">
                  <div className="inline-flex w-full items-center justify-center gap-2 py-3">
                    <p className="font-semibold text-gray-500">
                      Total de ingresos
                    </p>
                    <GenericToggle
                      changeState={() =>
                        pieStatus === 'sales'
                          ? setPieStatus('incomes')
                          : setPieStatus('sales')
                      }
                      currentState={pieStatus === 'sales'}
                    />
                    <p className="font-semibold text-gray-700">
                      Total de ventas
                    </p>
                  </div>

                  <div className="mb-20 h-96 flex-col justify-center">
                    <h3 className="pb-2 text-center font-semibold text-gray-600">
                      {pieStatus === 'incomes'
                        ? 'Total de ingresos'
                        : 'Total de ventas'}
                    </h3>
                    <div className="flex justify-center pb-2 font-semibold">
                      {formatPrice(
                        pieStatus === 'incomes'
                          ? convertCurrency(
                              {
                                price: totals.incomes,
                                codeCurrency: mainCurrency ?? 'CUP',
                              },
                              activeCurrency
                            )
                          : convertCurrency(
                              {
                                price: totals.sales,
                                codeCurrency: mainCurrency ?? 'CUP',
                              },
                              activeCurrency
                            )
                      )}
                    </div>
                    <Pie
                      className={`${
                        pieStatus === 'incomes' ? 'cursor-pointer' : ''
                      }`}
                      data={{
                        labels:
                          pieStatus === 'incomes' ? incomesLabel : salesLabel,
                        datasets: [
                          {
                            label: 'Ventas totales',
                            data:
                              pieStatus === 'incomes'
                                ? incomes.map(
                                    (itm) =>
                                      convertCurrency(
                                        {
                                          price: itm,
                                          codeCurrency: mainCurrency ?? 'CUP',
                                        },
                                        activeCurrency
                                      ).price
                                  )
                                : sales.map(
                                    (itm) =>
                                      convertCurrency(
                                        {
                                          price: itm,
                                          codeCurrency: mainCurrency ?? 'CUP',
                                        },
                                        activeCurrency
                                      ).price
                                  ),
                            backgroundColor:
                              pieStatus === 'incomes'
                                ? colorIncomesPie
                                : colorSalesPie,
                            datalabels: {
                              color: 'black',
                              formatter: (_, ctx) => {
                                return `${
                                  pieStatus === 'incomes'
                                    ? incomesLabel[ctx.dataIndex]
                                    : salesLabel[ctx.dataIndex]
                                }`
                              },
                            },
                          },
                        ],
                      }}
                      // onClick={(event) => {
                      //   if (pieStatus === 'incomes') {
                      //     //@ts-ignore
                      //     /*setselectedIncome(
                      //       graphsData?.groupData?.find(
                      //         (business) =>
                      //           business.totalIncomesMainCurrency ===
                      //           getElementAtEvent(chartRef?.current!, event)[0]
                      //             .element.$context.parsed
                      //       )?.name
                      //     );*/
                      //   }
                      // }}
                      options={{
                        plugins: {
                          legend: { display: false },
                          datalabels: {
                            display: true,
                            anchor: 'center',
                          },
                        },
                        responsive: true,
                        maintainAspectRatio: false,
                      }}
                      ref={chartRef}
                    />
                  </div>
                </div>

                {/* Total de ingresos */}

                <div className="relative ml-4 mr-4 rounded-md border-2 border-slate-300 md:ml-10 md:mr-0">
                  {selectedIncome !== 'general' && (
                    <div className="absolute right-4 top-4">
                      <Button
                        action={() => setselectedIncome('general')}
                        color="slate-600"
                        name="Ver general"
                        type="button"
                      />
                    </div>
                  )}
                  <table className="m-4 h-auto w-96 divide-y divide-gray-300 border-b border-gray-200 p-4">
                    <thead>
                      <tr className="w-full p-4 font-medium text-gray-900">
                        <th>
                          {selectedIncome === 'general'
                            ? 'Total de ingresos'
                            : `Total de ingresos en ${selectedIncome}`}
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr>
                        <td className="py-4 pl-4 pr-3 text-sm sm:pl-6 md:pl-0">
                          {/* Monedas a mostrar */}
                          {selectedIncome === 'general' ? (
                            <div className="flex w-full flex-col px-8">
                              {totalIncomes !== undefined &&
                                totalIncomes.map(
                                  (
                                    element: {
                                      codeCurrency: string
                                      transfer: number
                                      cash: number
                                    },
                                    idx
                                  ) => (
                                    <div
                                      className="my-5 flex w-full items-center justify-between"
                                      key={idx}
                                    >
                                      <p className="w-1/3 font-bold">
                                        {element.codeCurrency}
                                      </p>
                                      <div className="flex w-2/3 flex-col items-center justify-end">
                                        {element.transfer > 0 && (
                                          <p className="my-1 font-bold">
                                            Transferencia
                                          </p>
                                        )}
                                        {element.cash > 0 && (
                                          <p className="my-1 font-bold">
                                            Efectivo
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  )
                                )}
                            </div>
                          ) : (
                            <div className="flex w-full flex-col px-8">
                              {getTotalIncomeFunction(
                                graphsData?.groupData?.filter(
                                  (itm) => itm.name === selectedIncome
                                ) ?? []
                              )?.map(
                                (
                                  element: {
                                    codeCurrency: string
                                    transfer: number
                                    cash: number
                                  },
                                  idx
                                ) => (
                                  <div
                                    className="my-5 flex w-full items-center justify-between"
                                    key={idx}
                                  >
                                    <p className="w-1/3 font-bold">
                                      {element.codeCurrency}
                                    </p>
                                    <div className="flex w-2/3 flex-col items-center justify-end">
                                      {element.transfer > 0 && (
                                        <p className="my-1 font-bold">
                                          Transferencia
                                        </p>
                                      )}
                                      {element.cash > 0 && (
                                        <p className="my-1 font-bold">
                                          Efectivo
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                          )}
                        </td>
                        <td className="py-4 pl-4 pr-3 text-sm sm:pl-6 md:pl-0">
                          {/* Precios a mostrar */}
                          {selectedIncome === 'general' ? (
                            <div className="flex w-full flex-col px-8">
                              {totalIncomes !== undefined &&
                                totalIncomes.map(
                                  (
                                    element: {
                                      codeCurrency: string
                                      transfer: number
                                      cash: number
                                    },
                                    idx
                                  ) => (
                                    <div
                                      className="my-5 flex flex-col items-end justify-center"
                                      key={idx}
                                    >
                                      {element.transfer > 0 && (
                                        <p
                                          className={cn(
                                            'my-1 whitespace-pre-wrap text-right text-sm font-bold text-gray-500',
                                            'sm:pr-6 md:pr-0'
                                          )}
                                        >
                                          {printPriceWithCommasAndPeriods(
                                            element.transfer.toFixed(2)
                                          )}
                                        </p>
                                      )}
                                      {element.cash > 0 && (
                                        <p
                                          className={cn(
                                            'my-1 whitespace-pre-wrap text-right text-sm font-bold',
                                            'text-gray-500 sm:pr-6 md:pr-0'
                                          )}
                                        >
                                          {printPriceWithCommasAndPeriods(
                                            element.cash.toFixed(2)
                                          )}
                                        </p>
                                      )}
                                    </div>
                                  )
                                )}
                            </div>
                          ) : (
                            <div className="flex w-full flex-col px-8">
                              {getTotalIncomeFunction(
                                graphsData?.groupData?.filter(
                                  (itm) => itm.name === selectedIncome
                                ) ?? []
                              )?.map(
                                (
                                  element: {
                                    codeCurrency: string
                                    transfer: number
                                    cash: number
                                  },
                                  idx
                                ) => (
                                  <div
                                    className="my-5 flex flex-col items-end justify-end"
                                    key={idx}
                                  >
                                    {element.transfer > 0 && (
                                      <p
                                        className={cn(
                                          'my-1 whitespace-pre-wrap text-right text-sm font-bold',
                                          'text-gray-500 sm:pr-6 md:pr-0'
                                        )}
                                      >
                                        {printPriceWithCommasAndPeriods(
                                          element.transfer.toFixed(2)
                                        )}
                                      </p>
                                    )}
                                    {element.cash > 0 && (
                                      <p
                                        className={cn(
                                          'my-1 whitespace-pre-wrap text-right text-sm font-bold',
                                          'text-gray-500 sm:pr-6 md:pr-0'
                                        )}
                                      >
                                        {printPriceWithCommasAndPeriods(
                                          element.cash.toFixed(2)
                                        )}
                                      </p>
                                    )}
                                  </div>
                                )
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* -------------------------------------------------- */}

          <div className="mb-10 h-96 flex-col justify-center">
            {graphsData?.groupData?.reduce(
              (total, items) => total + items.grossProfit,
              0
            ) === 0 ||
            graphsData?.groupData?.reduce(
              (total, items) => total + items.totalCost,
              0
            ) === 0 ? (
              ''
            ) : (
              <Chart
                data={{
                  labels: profitLabel,
                  datasets: [
                    {
                      yAxisID: 'y',
                      label: 'Ganancia',
                      data: salesProfit ?? 0,
                      backgroundColor: 'rgba(2, 2, 253, 0.578)',
                      barPercentage: 0.6,
                    },
                    {
                      yAxisID: 'y',
                      label: 'Costo',
                      data: salesCost ?? 0,
                      backgroundColor: 'rgba(255, 0, 0, 0.507)',
                      barPercentage: 0.6,
                    },
                  ],
                }}
                options={{
                  plugins: {
                    legend: {
                      position: 'right',
                    },
                    datalabels: {
                      display: false,
                    },
                  },
                  responsive: true,
                  maintainAspectRatio: false,
                  indexAxis: 'y' as const,
                  scales: {
                    x: {
                      min: 0,
                    },
                    y: {
                      stacked: false,
                    },
                  },
                }}
                type="bar"
              />
            )}
          </div>

          {graphsData?.businessData &&
            !(
              graphsData?.businessData?.maxValue === 0 &&
              graphsData?.businessData?.minValue === 0
            ) && (
              <>
                <div className="m-auto w-full max-w-7xl px-5">
                  <Line
                    data={{
                      datasets: [
                        {
                          label: 'Ventas',
                          data: graphsData?.businessData?.totalSales.map(
                            (itm) =>
                              convertCurrency(
                                {
                                  price: itm,
                                  codeCurrency: mainCurrency ?? 'CUP',
                                },
                                activeCurrency
                              ).price
                          ),
                          tension: 0.3,
                          borderColor: 'rgb(81, 154, 68)',
                          pointRadius: 2,
                          backgroundColor: 'rgb(160, 200, 154,0.3)',
                        },
                        {
                          label: 'Costos',
                          data: graphsData.businessData.totalCost.map(
                            (itm) =>
                              convertCurrency(
                                {
                                  price: itm,
                                  codeCurrency: mainCurrency ?? 'CUP',
                                },
                                activeCurrency
                              ).price
                          ),
                          tension: 0.3,
                          borderColor: 'rgb(115, 27, 10)',
                          pointRadius: 3,
                          backgroundColor: 'rgb(209, 44, 10,0.3)',
                        },
                        {
                          label: 'Ganancia Bruta',
                          data: graphsData?.businessData?.grossProfit.map(
                            (itm) =>
                              convertCurrency(
                                {
                                  price: itm,
                                  codeCurrency: mainCurrency ?? 'CUP',
                                },
                                activeCurrency
                              ).price
                          ),
                          tension: 0.3,
                          borderColor: 'rgb(9, 106, 106)',
                          pointRadius: 4,
                          backgroundColor: 'rgb(15, 216, 216,0.3)',
                        },
                      ],
                      labels: graphsData?.businessData?.axisLabel,
                    }}
                    options={{
                      responsive: true,
                      events: ['mousemove', 'click'],
                      scales: {
                        y: {
                          min: getMaxValue(graphsData.businessData.minValue),
                          max: getMaxValue(graphsData.businessData.maxValue),
                          ticks: {
                            callback(tickValue: number | string) {
                              return '$' + prettyNumber(Number(tickValue))
                            },
                          },
                        },
                      },
                      plugins: {
                        legend: {
                          display: true,
                        },
                        datalabels: {
                          display: false,
                        },
                        tooltip: {
                          callbacks: {
                            title(tooltipItems) {
                              return `${tooltipItems[0].dataset?.label} ${tooltipItems[0].label}`
                            },
                            label(item) {
                              return formatPrice({
                                price: Number(item.raw),
                                codeCurrency: activeCurrency,
                              })
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
              </>
            )}
        </>
      )}
    </div>
  )
}

const HomeProduction = () => {
  const { getHomeData, getOrder, allOrders, order, homeLoading, isLoading } =
    useServerOrderProd()
  const { control, watch } = useForm()

  const activeOrder = watch('activeOrder')
  useEffect(() => {
    if (allOrders.length === 0) {
      getHomeData()
    } else {
      getOrder(activeOrder)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrder])

  //Data for buttons filter ----------------------------------------------------------------------------
  const activeOrdersData: InlineRadioData[] = []
  allOrders.map((item) =>
    activeOrdersData.push({
      label: moment(item.createdAt).format('DD [de] MMMM'),
      value: item.id,
    })
  )
  //----------------------------------------------------------------------------------------------------

  //Graph data --------------------------------------------------------------------------------------------
  const labels: string[] = ['Producción Total']
  const data: { part: number; total: number; percent: number }[] = [
    {
      part: order?.productionOrder.totalProduced ?? 0,
      total: order?.productionOrder.totalGoalQuantity ?? 1,
      percent: getPercent(
        order?.productionOrder.totalProduced ?? 0,
        order?.productionOrder.totalGoalQuantity ?? 1
      ),
    },
  ]
  const colors: string[] = ['rgb(69, 38, 225)']

  order?.endProducts?.forEach((item) => {
    labels.push(item.name)
    data.push({
      part: item.realProduced,
      total: item.goalQuantity,
      percent: getPercent(item.realProduced, item.goalQuantity),
    })
    colors.push('#858ae8')
  })

  //-------------------------------------------------------------------------------------------------------

  if (homeLoading) return <SpinnerLoading text="Cargando datos ..." />
  return (
    <div className="">
      <div className="my-5">
        <InlineRadio
          adjustContent
          control={control}
          data={activeOrdersData}
          defaultValue={activeOrdersData[0]?.value}
          name="activeOrder"
        />
      </div>
      <div className="flex">
        {isLoading ? (
          <div className="flex w-full justify-center">
            <SpinnerLoading text="Cargando datos de la orden ..." />
          </div>
        ) : allOrders.length === 0 ? (
          <div className="flex w-full justify-center">
            <EmptyList
              subTitle="En este momento no hay órdenes activas"
              title="Si datos que mostrar"
            />
          </div>
        ) : (
          <Chart
            data={{
              labels: labels,
              datasets: [
                {
                  yAxisID: 'y',
                  label: 'Estado de Producción',
                  data: data.map((item) => item.percent),
                  backgroundColor: colors,
                  datalabels: {
                    color: 'white',
                    formatter: (value, ctx) =>
                      `${data[ctx.dataIndex].part} / ${
                        data[ctx.dataIndex].total
                      } (${data[ctx.dataIndex].percent}%)`,
                  },
                },
              ],
            }}
            options={{
              plugins: {
                datalabels: {
                  display: true,
                },
              },
              responsive: true,
              maintainAspectRatio: true,
              indexAxis: 'y' as const,
              scales: {
                x: {
                  max: 100,
                  min: 0,
                },
              },
            }}
            style={{ height: 100, display: 'flex' }}
            type="bar"
          />
        )}
      </div>
    </div>
  )
}

const HomeCommon = ({
  onErrorChange,
  onRefetch,
}: {
  onErrorChange?: (_err: boolean) => void
  onRefetch?: (_fn: RefetchFn) => void
}) => {
  // const { graphsData, loading } = useAppSelector((state) => state.auxiliar)
  const { control, watch } = useForm()
  // const dispatch = useAppDispatch()
  const { business } = useAppSelector((state) => state.init)

  const mainCurrency = business?.mainCurrency
  const availableCurrencies = business?.availableCurrencies
  const { convertCurrency, formatPrice } = useProduct()

  const [activeCurrency, setActiveCurrency] = useState<string>(
    mainCurrency ?? 'CUP'
  )

  const dateFilter = watch('dateFilter') ?? 'week'

  const params: GetGraphDataParams | undefined = useMemo(() => {
    if (!business) return undefined

    const base = { businessMode: 'single' as const, business }

    return {
      ...base,
      dateMode: dateFilter,
    }
  }, [dateFilter, business])

  const {
    data: graphsData,
    isLoading,
    isError,
    refetch,
  } = useGraphData(params) as {
    data: GraphDataInterface | undefined
    isLoading: boolean
    isError: boolean
    refetch: RefetchFn
  }

  useEffect(() => {
    onErrorChange?.(isError)
  }, [isError, onErrorChange])

  useEffect(() => {
    onRefetch?.(refetch)
  }, [refetch, onRefetch])

  // useEffect(() => {
  //   if (dateFilter !== graphsData?.dateMode || !graphsData?.businessData) {
  //     dispatch(getGraphData({ businessMode: 'single', dateMode: dateFilter }))
  //   }
  // }, [dateFilter])

  //Data for MostSelled table ------------------------------------------------------------------------
  const titles = [
    'Nombre',
    'Precio de Venta',
    'Cantidad Vendida',
    'Cantidad Disponible',
  ]
  const displayData: DataTableInterface[] = useMemo(() => {
    if (!graphsData?.businessData?.mostSelled) return []
    return graphsData.businessData.mostSelled.map((item) => ({
      payload: {
        Nombre: item.name,
        'Precio de Venta': `${item.prices[0].price} ${item.prices[0].codeCurrency}`,
        'Cantidad Vendida': item.totalSale,
        'Cantidad Disponible': item.stockLimit ? (
          item.amountRemain
        ) : (
          <UnlimitedBadge />
        ),
      },
    }))
  }, [graphsData])

  //---------------------------------------------------------------------------------------------------

  //Btn info --------------------------------------------------------------
  const btnInfo = [
    { value: 'week', label: 'Esta semana' },
    { value: 'month', label: 'Este mes' },
    { value: 'year', label: 'Este año' },
  ]
  //--------------------------------------------------------------------------

  //Apellido tendencia
  const rangeName = () => {
    switch (graphsData?.dateMode) {
      case 'week':
        return 'semanal'
      case 'month':
        return 'mensual'
      case 'year':
        return 'anual'
      default:
        break
    }
  }

  const lineTotals =
    (graphsData?.businessData?.totalCost?.reduce(
      (total, itm) => total + itm,
      0
    ) ?? 0) +
    (graphsData?.businessData?.totalSales?.reduce(
      (total, itm) => total + itm,
      0
    ) ?? 0) +
    (graphsData?.businessData?.grossProfit?.reduce(
      (total, itm) => total + itm,
      0
    ) ?? 0) +
    (graphsData?.businessData?.totalIncomes?.reduce(
      (total, itm) => total + itm,
      0
    ) ?? 0)

  const dinamicData = useMemo(() => {
    const minAxisValue = convertCurrency(
      {
        price: graphsData?.businessData?.minValue,
        codeCurrency: mainCurrency ?? 'CUP',
      },
      activeCurrency
    ).price
    const maxAxisValue = convertCurrency(
      {
        price: graphsData?.businessData?.maxValue,
        codeCurrency: mainCurrency ?? 'CUP',
      },
      activeCurrency
    ).price

    return { minAxisValue, maxAxisValue }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCurrency, graphsData])

  return (
    <div className="grid grid-cols-2 gap-6">
      <div className="col-span-2">
        <div className="m-auto sm:flex sm:w-3/4">
          <InlineRadio
            control={control}
            data={btnInfo}
            defaultValue={graphsData?.dateMode ?? 'week'}
            name="dateFilter"
          />
        </div>
        {isLoading ? (
          <SpinnerLoading className="h-[24.1rem]" />
        ) : (
          <div>
            {lineTotals !== 0 && (
              <div className="flex items-center justify-center gap-5 pb-5">
                {availableCurrencies?.map((curr, index) => {
                  return (
                    <Button
                      action={() => {
                        setActiveCurrency(curr.code)
                      }}
                      color={
                        activeCurrency === curr.code ? 'slate-600' : 'white'
                      }
                      key={index}
                      name={curr.code}
                      textColor={
                        activeCurrency === curr.code ? 'white' : 'slate-600'
                      }
                    />
                  )
                })}
              </div>
            )}

            {graphsData?.dateRange && (
              <div className="flex items-center justify-center gap-5 pb-5">
                <div className="rounded-md border border-gray-300 px-2 py-1">
                  {moment(graphsData?.dateRange.dateFrom).format(
                    graphsData.dateMode === 'year'
                      ? 'D [de] MMMM YYYY'
                      : 'D [de] MMMM'
                  )}
                </div>
                <ChevronRightIcon className="h-5 text-gray-400" />{' '}
                <div className="rounded-md border border-gray-300 px-2 py-1">
                  {moment(graphsData?.dateRange.dateTo).format(
                    graphsData.dateMode === 'year'
                      ? 'D [de] MMMM YYYY'
                      : 'D [de] MMMM'
                  )}
                </div>
              </div>
            )}

            {lineTotals !== 0 && (
              <Line
                data={{
                  datasets: [
                    {
                      label: 'Ventas',
                      data: graphsData?.businessData?.totalSales.map(
                        (itm) =>
                          convertCurrency(
                            { price: itm, codeCurrency: mainCurrency ?? 'CUP' },
                            activeCurrency
                          ).price
                      ),
                      tension: 0.3,
                      borderColor: 'rgb(81, 154, 68)',
                      pointRadius: 2,
                      backgroundColor: 'rgb(160, 200, 154,0.3)',
                    },
                    {
                      label: 'Costos',
                      data: graphsData?.businessData?.totalCost.map(
                        (itm) =>
                          convertCurrency(
                            { price: itm, codeCurrency: mainCurrency ?? 'CUP' },
                            activeCurrency
                          ).price
                      ),
                      tension: 0.3,
                      borderColor: 'rgb(115, 27, 10)',
                      pointRadius: 3,
                      backgroundColor: 'rgb(209, 44, 10,0.3)',
                    },
                    {
                      label: 'Ganancia Bruta',
                      data: graphsData?.businessData?.grossProfit.map(
                        (itm) =>
                          convertCurrency(
                            { price: itm, codeCurrency: mainCurrency ?? 'CUP' },
                            activeCurrency
                          ).price
                      ),
                      tension: 0.3,
                      borderColor: 'rgb(9, 106, 106)',
                      pointRadius: 4,
                      backgroundColor: 'rgb(15, 216, 216,0.3)',
                    },
                    {
                      label: 'Ingresos',
                      data: graphsData?.businessData?.totalIncomes.map(
                        (itm) =>
                          convertCurrency(
                            { price: itm, codeCurrency: mainCurrency ?? 'CUP' },
                            activeCurrency
                          ).price
                      ),
                      tension: 0.3,
                      pointRadius: 4,
                      backgroundColor: 'rgba(234, 94, 39, 0.2)',
                      borderColor: 'rgba(234, 94, 39, 1)',
                    },
                  ],
                  labels: graphsData?.businessData?.axisLabel,
                }}
                options={{
                  responsive: true,
                  events: ['mousemove', 'click'],
                  scales: {
                    y: {
                      min: getMaxValue(dinamicData.minAxisValue),
                      max: getMaxValue(dinamicData.maxAxisValue),
                      ticks: {
                        callback(tickValue) {
                          return '$' + prettyNumber(Number(tickValue))
                        },
                      },
                    },
                  },
                  plugins: {
                    tooltip: {
                      callbacks: {
                        title(tooltipItems) {
                          return `${tooltipItems[0].dataset?.label} ${tooltipItems[0].label}`
                        },
                        label(item) {
                          return formatPrice({
                            price: Number(item.raw),
                            codeCurrency: activeCurrency,
                          })
                        },
                      },
                    },
                    legend: {
                      display: true,
                    },
                    datalabels: {
                      display: false,
                    },
                  },
                }}
                style={{ maxHeight: '22rem' }}
              />
            )}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-6">
        <div className="h-fit">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-bold text-gray-900">
              Tendencia {rangeName()}
            </h1>
            <PresentationChartLineIcon className="h-6 animate-pulse" />
          </div>
          <div
            className={cn(
              'max-h-[22rem] overflow-y-auto border border-gray-200 bg-white scrollbar-thin',
              'scrollbar-thumb-gray-400'
            )}
          >
            {displayData.length === 0 ? (
              <div className="mt-4">
                <EmptyList />
              </div>
            ) : (
              <GenericTable
                loading={isLoading}
                tableData={displayData}
                tableTitles={titles}
              />
            )}
          </div>
        </div>
        {business && <PendingDispatch businessId={business?.id} />}
      </div>
      <div className="flex flex-col gap-6">
        <LastOperationsCard />
        <UnderLimitProductsCard />
      </div>
    </div>
  )
}

export default function HomePage() {
  const navigate = useNavigate()
  const { business, user } = useAppSelector((state) => state.init)

  const [isErrorGraphs, setIsErrorGraphs] = useState(false)
  const [refetchGraphs, setRefetchGraphs] = useState<() => void>()

  const hasError = isErrorGraphs

  const getErrorDetails = () => {
    const errors = []
    if (isErrorGraphs)
      errors.push('Error al cargar los detalles de las gráficas')

    return errors
  }

  let content

  if (
    business?.mode === 'GROUP' &&
    user?.roles.some((rol: { code: string }) => rol.code === 'GROUP_OWNER')
  ) {
    content = (
      <HomeGroup
        onErrorChange={setIsErrorGraphs}
        onRefetch={() => setRefetchGraphs}
      />
    )
  } else if (business?.type === 'PRODUCTION') {
    content = <HomeProduction />
  } else {
    content = (
      <HomeCommon
        onErrorChange={setIsErrorGraphs}
        onRefetch={() => setRefetchGraphs}
      />
    )
  }

  const handleGoBack = () => {
    navigate('/')
  }

  const handleRetry = () => {
    if (isErrorGraphs) refetchGraphs?.()
  }

  return (
    <ErrorOverlay
      description={`No pudimos cargar los gráficos }. 
        Esto podría deberse a un problema temporal de red o a que los datos no estén disponibles.`}
      errorDetails={getErrorDetails()}
      isVisible={hasError}
      onDismiss={handleGoBack}
      onRetry={handleRetry}
      title="Error al cargar los detalles de las gráficas"
    >
      {content}
    </ErrorOverlay>
  )
}
